/**
 * Generic entity thread (#45 issues, #49 calendar events): the discussion attached to one entity, built from the
 * normal chat pieces (MessageItem, Composer, messageStore, realtime). The entity decides who can read, post and
 * moderate; this component only renders what the API allows.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Bell, BellOff, ChevronDown, ChevronRight, MessagesSquare } from 'lucide-react';
import type { EntityEventMeta, Message } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useMembers, useWorkspaceId } from '@/lib/hooks';
import { messageStore, useConversationState } from '@/lib/messageStore';
import { queryClient } from '@/lib/queryClient';
import { dt, messageTimestamp } from '@/lib/format';
import { useUi } from '@/lib/store';
import { Button, ErrorBox, Spinner } from '@/components/ui';
import { MessageTime } from '@/components/time/RelativeTime';
import { Composer } from '@/features/chat/Composer';
import { MessageItem, PendingItem } from '@/features/chat/MessageItem';
import { onListKeyDown } from '@/features/chat/reply';
import { ThreadPrefsProvider } from '@/features/chat/InlineThread';
import { runPositions } from '@/features/chat/bubbles';
import { useLiveEntityConversation, useThreadHost } from './liveConversation';

export const entityThreadKey = (ws: string, type: string, id: string) => ['entity-thread', ws, type, id] as const;

export function useEntityThread(type: string, entityId: string) {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: entityThreadKey(ws ?? '', type, entityId), queryFn: () => api.entityThread(ws!, type, entityId), enabled: Boolean(ws && entityId) });
}

/** A system line: "Sarah Patel changed the status from New to In progress · 14:05". Subtle lines (RSVPs) are quieter. */
/** t564u: who did it opens their person sheet, as names do everywhere else. */
function Actor({ id, name, className }: { id: string | null | undefined; name: string; className: string }) {
  const open = useUi(s => s.openPerson);
  if (!id) return <strong className={className}>{name}</strong>;
  return <button type="button" className={clsx(className, 'cursor-pointer rounded hover:underline focus-visible:outline-2')} data-testid="thread-event-actor"
    onClick={e => { e.preventDefault(); e.stopPropagation(); open(id); }} aria-label={`View ${name}’s profile`}>{name}</button>;
}

export function SystemLine({ m, actor, centred = false }: { m: Message; actor: string;
  /** t359u parity: the phone (bubble) layout centres it between the bubbles, as the app's entity threads do. */ centred?: boolean }) {
  const meta = (m.metadata ?? {}) as EntityEventMeta;
  const count = Number((meta as { count?: number }).count ?? 1);
  if (centred) return (
    <p id={`m-${m.id}`} data-testid="thread-event" data-subtle={meta.subtle || undefined} title={count > 1 ? `Changed ${count} times in a few minutes` : undefined}
      className={clsx('px-8 py-1.5 text-center text-xs leading-5', meta.subtle ? 'text-slate-400 dark:text-slate-500' : 'text-slate-500 dark:text-slate-400')}>
      {!meta.subtle && actor && <><Actor id={m.sender_id} name={actor} className="font-semibold text-slate-600 dark:text-slate-300" /> </>}{m.body}
      <span className="whitespace-nowrap text-slate-400 dark:text-slate-500"> · <MessageTime iso={m.created_at} inline testId="thread-event-time" /></span>
    </p>
  );
  return (
    <div id={`m-${m.id}`} data-testid="thread-event" data-subtle={meta.subtle || undefined} title={count > 1 ? `Changed ${count} times in a few minutes` : undefined}
      className={clsx('flex items-start gap-2 px-5 py-1 text-xs', meta.subtle ? 'text-slate-400 dark:text-slate-500' : 'text-slate-500 dark:text-slate-400')}>
      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300 dark:bg-slate-600" aria-hidden />
      <p className="min-w-0 flex-1 leading-5">
        {!meta.subtle && <><Actor id={m.sender_id} name={actor} className="font-medium text-slate-700 dark:text-slate-200" /> </>}{m.body}
                <time dateTime={m.created_at} title={dt(m.created_at).toFormat('d LLL yyyy, HH:mm')} className="ml-1.5 whitespace-nowrap text-slate-400 dark:text-slate-500">· {messageTimestamp(m.created_at)}</time>
      </p>
    </div>
  );
}

/** Three or more quiet lines in a row (e.g. a burst of RSVPs) fold into one expandable summary. */
export const QUIET_GROUP_MIN = 3;
type Row = { kind: 'msg'; m: Message; i: number } | { kind: 'quiet'; items: Message[] };
export function groupQuiet(messages: Message[]): Row[] {
  const out: Row[] = [];
  let run: Message[] = [];
  const flush = () => {
    if (run.length >= QUIET_GROUP_MIN) out.push({ kind: 'quiet', items: run });
    else for (const m of run) out.push({ kind: 'msg', m, i: messages.indexOf(m) });
    run = [];
  };
  messages.forEach((m, i) => {
    if (m.kind === 'system' && (m.metadata as EntityEventMeta | null)?.subtle) { run.push(m); return; }
    flush();
    out.push({ kind: 'msg', m, i });
  });
  flush();
  return out;
}

export function quietSummary(items: { body: string }[]): string {
  const preview = items.slice(-2).map(m => m.body).join(', ');
  return `${items.length} updates · ${preview}${items.length > 2 ? ` and ${items.length - 2} more` : ''}`;
}

function QuietGroup({ items, centred = false }: { items: Message[]; centred?: boolean }) {
  const [open, setOpen] = useState(false);
  if (centred) return (
    <div data-testid="thread-quiet-group">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        className="flex w-full items-center justify-center gap-1 px-8 py-1.5 text-xs text-slate-400 active:text-slate-600 dark:text-slate-500">
        <span className="min-w-0 truncate">{quietSummary(items)}</span>
        {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
      </button>
      {open && items.map(m => <SystemLine key={m.id} m={m} actor="" centred />)}
    </div>
  );
  return (
    <div data-testid="thread-quiet-group">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}
        className="flex w-full items-center gap-2 px-5 py-1 text-left text-xs text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300">
        {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
        <span className="min-w-0 truncate">{quietSummary(items)}</span>
      </button>
      {open && items.map(m => <SystemLine key={m.id} m={m} actor="" />)}
    </div>
  );
}

/**
 * t359u parity: an entity thread's top-level messages in the phone layout, as the app draws them - chat bubbles (yours
 * on the right, names over others' runs), centred event lines with who did it, quiet runs folded, and "N replies"
 * under a message opening its thread (no inline replies on the phone, as in phone Chat).
 */
export function BubbleRows({ messages }: { messages: Message[] }) {
  const { byId } = useMembers();
  const runs = useMemo(() => runPositions(messages), [messages]);
  return (
    <>
      {groupQuiet(messages).map(row => {
        if (row.kind === 'quiet') return <QuietGroup key={row.items[0].id} items={row.items} centred />;
        const { m } = row;
        return m.kind === 'system'
          ? <SystemLine key={m.id} m={m} actor={byId.get(m.sender_id)?.display_name ?? 'Someone'} centred />
          : <MessageItem key={m.id} m={m} compact={!runs.get(m.id)?.first} bubble={{ ...(runs.get(m.id) ?? { first: true, last: true }), names: true }} />;
      })}
    </>
  );
}

/**
 * `fit` (t299u): the box sizes to its messages instead of filling a fixed height - a one-line empty state, the composer
 * straight under the last message, and long threads scroll inside a capped list. Without it the list fills the parent.
 */
export function EntityThread({ type, entityId, title = 'Discussion', emptyText = 'No messages yet. Start the discussion below.', className, headerExtra, onFollowChange, fit = false, phone = false }: {
  type: string; entityId: string; title?: string; emptyText?: string; className?: string; headerExtra?: ReactNode; onFollowChange?: (following: boolean) => void; fit?: boolean;
  /**
   * t359u: the phone discussion tab, identical to the app's (apps/mobile/src/chat/EntityThread): a slim Follow bar on
   * the right, chat bubbles (yours on the right), and "Write an update…". Opt-in, so desktop and other pages keep rows.
   */
  phone?: boolean;
}) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const { byId } = useMembers();
  const info = useEntityThread(type, entityId);
  const convId = info.data?.conversation_id;
  const state = useConversationState(convId);
  const listRef = useRef<HTMLDivElement>(null);

  useLiveEntityConversation(convId);

  const messages = useMemo(() => state.messages.filter(m => !m.parent_id), [state.messages]);
  // t157u: replies open in the shared thread panel (inline previews under each message), like Chat.
  const addRoots = useThreadHost(s => s.add);
  useEffect(() => { if (convId && messages.length) addRoots(convId, messages.map(m => m.id), Boolean(info.data?.can_post)); }, [convId, messages, addRoots, info.data?.can_post]);
  const last = messages.at(-1)?.sequence ?? 0;
  // Scroll the list itself to the newest message; scrollIntoView also scrolled the page down to the box on open.
  useEffect(() => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; }, [messages.length, state.pending.length]);
  useEffect(() => { if (convId && last > (info.data?.last_read_sequence ?? 0)) void api.markRead(convId, last).catch(() => undefined); }, [convId, last, info.data?.last_read_sequence]);

  const follow = useMutation({
    mutationFn: (on: boolean) => api.followEntityThread(ws!, type, entityId, on),
    onSuccess: r => { onFollowChange?.(r.following); void queryClient.invalidateQueries({ queryKey: entityThreadKey(ws ?? '', type, entityId) }); toast(r.following ? 'You’ll be notified about new messages' : 'You won’t be notified about new messages', 'success'); },
    onError: e => toast(errorMessage(e), 'error'),
  });

  const count = messages.filter(m => m.kind === 'user' && !m.deleted_at).length;
  const followBtn = info.data && (phone
    ? <button type="button" onClick={() => follow.mutate(!info.data!.following)} disabled={follow.isPending} role="switch" aria-checked={info.data.following} data-testid="entity-thread-follow"
        className="flex min-h-9 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold text-slate-600 active:bg-slate-100 dark:text-slate-300 dark:active:bg-slate-800">
        {info.data.following ? <BellOff className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}{info.data.following ? 'Following' : 'Follow'}
      </button>
    : <Button size="sm" variant="secondary" onClick={() => follow.mutate(!info.data!.following)} loading={follow.isPending} aria-pressed={info.data.following}
        title={info.data.following ? 'Stop notifications for new messages' : 'Get notified about new messages'}>
        {info.data.following ? <><BellOff className="h-3.5 w-3.5" />Unfollow</> : <><Bell className="h-3.5 w-3.5" />Follow</>}
      </Button>);
  // On the phone a message's replies show as "N replies" and open in the thread panel (as phone Chat and the app).
  const section = (
    <section className={clsx('min-h-0 flex-col', phone ? 'bg-white dark:bg-slate-950' : 'rounded-xl bg-white ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800', className)} aria-label={title} data-testid="entity-thread">
      {phone ? <header className="flex items-center justify-end border-b border-slate-100 px-3 py-1 dark:border-slate-800">{followBtn}</header> : (
      <header className="flex items-center gap-2 border-b border-slate-200 px-4 py-2.5 dark:border-slate-800">
        <MessagesSquare className="h-4 w-4 text-slate-500" />
        <h2 className="font-semibold">{title}</h2>
        {count > 0 && <span className="text-xs text-slate-500">{count} {count === 1 ? 'message' : 'messages'}</span>}
        <span className="ml-auto flex items-center gap-2">
          {headerExtra}
          {followBtn}
        </span>
      </header>)}
      <div ref={listRef} className={clsx('scroll-thin overflow-y-auto outline-none', fit ? 'max-h-[min(32rem,calc(100dvh-16rem))] py-1' : phone ? 'min-h-0 flex-1 px-1 py-2' : 'min-h-[12rem] flex-1 py-2')} data-message-list="entity" tabIndex={0} aria-label={`${title} messages`} onKeyDown={onListKeyDown}>
        <ErrorBox error={info.error ? errorMessage(info.error) : state.error ?? null} />
        {(info.isLoading || (convId && (state.status === 'idle' || state.status === 'loading'))) && <div className="flex justify-center p-6"><Spinner /></div>}
        {convId && state.nextBefore !== null && <div className="flex justify-center py-1"><Button size="sm" variant="ghost" loading={state.loadingOlder} onClick={() => void messageStore.loadOlder(convId)}>Show earlier messages</Button></div>}
        {convId && state.status === 'ready' && messages.length === 0 && <p className={clsx('text-sm text-slate-500', fit ? 'px-4 py-2' : phone ? 'px-6 py-10 text-center' : 'px-5 py-6 text-center')} data-testid="entity-thread-empty">{emptyText}</p>}
        {phone ? <BubbleRows messages={messages} /> : groupQuiet(messages).map(row => {
          if (row.kind === 'quiet') return <QuietGroup key={row.items[0].id} items={row.items} />;
          const { m, i } = row;
          const prev = messages[i - 1];
          return m.kind === 'system'
            ? <SystemLine key={m.id} m={m} actor={byId.get(m.sender_id)?.display_name ?? 'Someone'} />
            : <MessageItem key={m.id} m={m} comment
                compact={Boolean(prev) && prev.kind === 'user' && prev.sender_id === m.sender_id && new Date(m.created_at).getTime() - new Date(prev.created_at).getTime() < 5 * 60_000} />;
        })}
        {convId && state.pending.filter(p => !p.parent_id).map(p => <PendingItem key={p.client_message_id} p={p} conversationId={convId} />)}
      </div>
      {convId && info.data?.can_post && (phone ? <div className="pt-1"><Composer key={convId} conversationId={convId} placeholder="Write an update…" /></div>
        : <Composer key={convId} conversationId={convId} placeholder="Write a message…" />)}
      {info.data && !info.data.can_post && <p className="border-t border-slate-200 px-4 py-3 text-xs text-slate-500 dark:border-slate-800">You can read this discussion, but only people taking part can post.</p>}
    </section>
  );
  return phone ? section : <ThreadPrefsProvider conversationId={convId ?? ''} canReply={Boolean(info.data?.can_post)} canReact={info.data?.can_react !== false}>{section}</ThreadPrefsProvider>;
}
